/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useState, ChangeEvent } from 'react';
import { AlertDialog } from '../Dialog/AlertDialog';
import { Trash } from '@jda/lui-common-icon-library-mui5';
import { Box, Checkbox, FormControlLabel, IconButton, Tooltip, Typography } from '@mui/material';
import { useStyles } from './styles';
import { CheckViewPermission } from '../CheckPermissions/CheckViewPermission';
import { IDeleteBtnProps } from '../../models/Common/DeleteBtn';
import {
  UNPROCESSABLE_ENTITY,
  CONSTRAINT_VIOLATION,
  GLOBAL,
  CONSEQUENCE_AWARE_DELETION,
  CAN_NOT_BE_UNDONE_MESSAGE,
  DELETE_GLOBAL_ROLE_CONFIRMATION
} from './constants';
import interpolateString from '../../utility/stringInterpolation';

const DeleteBtn = ({
  dialog,
  onDelete,
  refreshOnSave,
  displaySnackbar,
  hideSnackbar,
  permissions,
  messages
}: IDeleteBtnProps) => {
  const { classes } = useStyles();
  const [showDialog, setShowDialog] = useState(false);
  const [showDialogLoader, setDialogHideLoader] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  const handleDeleteIconClick = () => {
    setShowDialog(true);
  };

  const handleChangeCheckBox = (e: ChangeEvent<HTMLInputElement>) => {
    setIsChecked(e.target.checked);
  };

  const handleDelete = async () => {
    setDialogHideLoader(true);
    hideSnackbar();
    try {
      await onDelete();
      setDialogHideLoader(false);
      refreshOnSave('');
      setShowDialog(false);
      displaySnackbar(messages.success, 'success');
    } catch (error: any) {
      setDialogHideLoader(false);
      setShowDialog(false);
      if (
        error.response &&
        error.response.status === UNPROCESSABLE_ENTITY &&
        messages.error &&
        messages.error.unprocessableEntity
      ) {
        displaySnackbar(messages.error.unprocessableEntity, 'error');
      } else if (error?.response?.status === CONSTRAINT_VIOLATION) {
        displaySnackbar(error?.response?.data?.message, 'error');
      } else {
        displaySnackbar(interpolateString([error.message], messages.error.failure), 'error');
      }
    }
  };

  return (
    <Box className={classes.deleteIcon}>
      <CheckViewPermission permission={permissions}>
        <Tooltip title="Delete">
          <Box data-testid="toolTip-sign">
            <Box id="deleteIcon" onClick={handleDeleteIconClick}>
              <IconButton aria-label="delete">
                <Trash fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Tooltip>
        {showDialog && (
          <AlertDialog
            dialogTitle={dialog.title}
            dialogContentText={dialog.content}
            recordName={dialog.recordName}
            primaryButtonText={dialog.primaryButtonText}
            primaryButtonColor='error'
            secondButtonText={dialog.secondaryButtonText}
            handleOnCancel={() => setShowDialog(false)}
            handleOnOk={handleDelete}
            showDialogLoader={showDialogLoader}
            {...(dialog.selectedRole === GLOBAL && {
              isPrimaryButtonDisabled: !isChecked,
              hideCanNotBeUndoneMessage: true,
              additionalContent: (
                <>
                  <Typography>{CONSEQUENCE_AWARE_DELETION}</Typography>
                  <Typography>{CAN_NOT_BE_UNDONE_MESSAGE}</Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color='primary'
                        name={'checkBox'}
                        checked={isChecked}
                        onChange={handleChangeCheckBox}
                      />
                    }
                    label={DELETE_GLOBAL_ROLE_CONFIRMATION}
                  />
                </>
              )
            })}
          />
        )}
      </CheckViewPermission>
    </Box>
  );
};

export default DeleteBtn;
